import { Component } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import get from 'lodash.get';
import {
    NUMBER_OF_PRODUCT_IMPRESSIONS,
    trackEcommerceProductImpressions,
    trackEcommerceProductClick,
} from '../../utils/tracking/searchBrowse/ecommerceTracking';

class BuySharedItemTrackingProviderComponent extends Component {
    constructor() {
        super();

        this.fireItemImpressionTracking = this.fireItemImpressionTracking.bind(this);
        this.fireItemClickTracking = this.fireItemClickTracking.bind(this);
        this.trackedItemImpressionIndexes = [];
        this.attemptedItemImpressionIndexes = [];
    }

    componentDidMount() {
        /**
         * GA requirment - first batch of impressions should fire on page load.
         */
        this.fireItemImpressionTracking(0);
    }

    componentDidUpdate(prevProps) {
        const regionalInfo = get(this.props, 'viewer.regionalInfo');
        const prevRegionalInfo = get(prevProps, 'viewer.regionalInfo');
        if (!prevRegionalInfo && regionalInfo) {
            this.attemptedItemImpressionIndexes.forEach(attemptedItemImpressionIndex => {
                this.fireItemImpressionTracking(attemptedItemImpressionIndex);
            });
            this.attemptedItemImpressionIndexes = [];
        }
    }

    trackProductImpression(startIndex) {
        const items = get(this.props, 'buyPage.edges');
        const pageType = get(this.props, 'buyPage.pageType');
        const userRegions = get(this.props, 'viewer.regionalInfo[0].regionsByZipCode') || [];

        trackEcommerceProductImpressions({
            items,
            startIndex,
            pageType,
            userRegions,
        });
    }

    fireItemImpressionTracking(index) {
        const hasRegionalInfo = !!get(this.props, 'viewer.regionalInfo');
        /**
         * Group items from index 0 to 19 to group with the number `0`, from 20 to 39 to group
         * 1 and so on.
         */
        const impressionGroup = Math.floor(index / NUMBER_OF_PRODUCT_IMPRESSIONS);
        if (!hasRegionalInfo) {
            this.attemptedItemImpressionIndexes.push(impressionGroup);
        } else if (!this.trackedItemImpressionIndexes.includes(impressionGroup)) {
            /**
             * Very important to track indexes that have already fired - `react-lazy-load` can fire
             * `contentVisible` handler multiple times for the same item.
             */
            this.trackProductImpression(impressionGroup * NUMBER_OF_PRODUCT_IMPRESSIONS);
            this.trackedItemImpressionIndexes.push(impressionGroup);
        }
    }

    fireItemClickTracking(index) {
        const pageType = get(this.props, 'buyPage.pageType');
        const items = get(this.props, 'buyPage.edges');
        const item = get(items, `[${index}].node.item`) || get(items, `[${index}].node`);
        const userRegions = get(this.props, 'viewer.regionalInfo[0].regionsByZipCode') || [];

        trackEcommerceProductClick({
            item,
            index,
            pageType,
            userRegions,
        });
    }

    render() {
        return this.props.children({
            fireItemImpressionTracking: this.fireItemImpressionTracking,
            fireItemClickTracking: this.fireItemClickTracking,
        });
    }
}

BuySharedItemTrackingProviderComponent.propTypes = {
    children: PropTypes.func,
    buyPage: PropTypes.object.isRequired,
    viewer: PropTypes.object,
};

export const BuySharedItemTrackingProvider = createFragmentContainer(
    BuySharedItemTrackingProviderComponent,
    {
        buyPage: graphql`
            fragment BuySharedItemTrackingProvider_buyPage on ItemSearchQueryConnection {
                pageType
                edges {
                    node {
                        item {
                            serviceId
                            title
                            categoryPath
                            browseUrl
                            categoryCode
                            contemporaryTrackingString
                            vertical
                            seller {
                                serviceId
                            }
                            displayPriceTracking: displayPrice(
                                page: searchAndBrowse
                                isTrade: $isTrade
                            ) {
                                convertedAmountList {
                                    amount
                                    currency
                                }
                                type
                            }
                            shipmentQuotes {
                                totalAmount {
                                    amount
                                }
                                serviceMethod {
                                    region {
                                        regionName
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `,
        viewer: graphql`
            fragment BuySharedItemTrackingProvider_viewer on Viewer {
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    regionsByZipCode {
                        displayName
                    }
                }
            }
        `,
    }
);
